import { useState, type FormEvent } from 'react';
import Reveal from '@/components/feature/Reveal';
import SectionHeading from './SectionHeading';

const SUBMIT_URL = 'https://readdy.ai/api/form/dah80ljp14a1h3mr6eqg';

const categories = [
  '公共事务与治理',
  '消费与市场秩序',
  '企业与行业观察',
  '医疗与健康安全',
  '环境与生态',
  '教育与培训',
  '其他线索',
];

export default function ContactSection() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [contentLength, setContentLength] = useState(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const payload = new FormData(form);

    const guard = String(payload.get('contact_alt') ?? '').trim();
    if (guard) {
      setStatus('success');
      form.reset();
      setContentLength(0);
      return;
    }
    payload.delete('contact_alt');

    const body = new URLSearchParams();
    payload.forEach((value, key) => {
      body.append(key, typeof value === 'string' ? value : 'Uncollectable');
    });

    setStatus('submitting');
    setErrorMsg('');

    try {
      const response = await fetch(SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const code = parsed?.code;
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');

      if (response.ok && code === 'OK' && !isSpam) {
        setStatus('success');
        form.reset();
        setContentLength(0);
      } else {
        setStatus('error');
        setErrorMsg(typeof serverMsg === 'string' && serverMsg.trim() ? serverMsg : '提交失败，请稍后重试。');
      }
    } catch {
      setStatus('error');
      setErrorMsg('网络异常，提交失败，请检查网络后重试。');
    }
  };

  return (
    <section id="submit" className="w-full bg-background-100 py-16 md:py-24 border-t border-background-200">
      <div className="w-full px-4 md:px-8">
        <SectionHeading
          kicker="SUBMIT"
          title="快速投稿：按格式填完即可提交"
          desc="这是三个渠道中处理最快的网页入口。填完后会生成投稿编号，请自行保存，后续可凭编号查询进度。"
        />

        <div className="mt-10 md:mt-14 grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-5 md:gap-6">
          <Reveal>
            <div className="h-full flex flex-col gap-4">
              <div className="p-6 rounded-lg bg-secondary-950">
                <h3 className="font-heading font-semibold text-lg text-background-50">其他投稿方式</h3>
                <ul className="mt-4 space-y-4 text-sm">
                  <li className="flex gap-3">
                    <i className="ri-mail-send-line mt-0.5 text-secondary-300" aria-hidden="true" />
                    <span className="text-background-200">
                      邮箱投稿：<br />
                      <span className="text-background-50">tip@baoliao51.example.com</span>
                    </span>
                  </li>
                  <li className="flex gap-3">
                    <i className="ri-eye-off-line mt-0.5 text-secondary-300" aria-hidden="true" />
                    <span className="text-background-200">
                      敏感线索建议走匿名通道，不采集手机号，凭一次性回访码联系。
                    </span>
                  </li>
                  <li className="flex gap-3">
                    <i className="ri-time-line mt-0.5 text-secondary-300" aria-hidden="true" />
                    <span className="text-background-200">值班编辑 48 小时内完成初核，节假日可能顺延。</span>
                  </li>
                </ul>
              </div>

              <div className="p-6 rounded-lg bg-accent-50 border border-accent-200">
                <h3 className="font-heading font-semibold text-base text-accent-900">安全提醒</h3>
                <p className="mt-2 text-sm text-accent-900/80 leading-relaxed">
                  本表单完全免费，平台不会以任何名义索要费用。请勿在正文中填写银行卡号、验证码等敏感信息。
                </p>
              </div>

              <div className="p-6 rounded-lg bg-background-50 border border-background-200">
                <h3 className="font-heading font-semibold text-base text-foreground-950">大附件怎么办</h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                  超过 20MB 的视频或多份文件，请先在此提交文字线索与说明，编辑回访时再通过邮箱补充，避免上传中断导致内容丢失。
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={80}>
            <div className="p-6 md:p-8 rounded-lg bg-background-50 border border-background-200">
              {status === 'success' ? (
                <div className="py-10 text-center">
                  <span className="mx-auto w-14 h-14 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                    <i className="ri-check-double-line text-3xl" aria-hidden="true" />
                  </span>
                  <h3 className="mt-5 font-heading font-semibold text-xl text-foreground-950">投稿已提交</h3>
                  <p className="mt-3 text-sm text-foreground-600 leading-relaxed max-w-md mx-auto">
                    感谢你的信任。值班编辑会在 48 小时内完成初核，请留意你留下的联系方式。如需继续投稿，可刷新页面重新填写。
                  </p>
                  <button
                    type="button"
                    onClick={() => setStatus('idle')}
                    className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-foreground-950 text-background-50 text-sm whitespace-nowrap cursor-pointer transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-refresh-line" aria-hidden="true" />
                    再投一条线索
                  </button>
                </div>
              ) : (
                <form
                  id="baoliao51-submit-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="space-y-5"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="title" className="block text-sm font-medium text-foreground-800 mb-1.5">
                        投稿标题 <span className="text-accent-600">*</span>
                      </label>
                      <input
                        id="title"
                        name="title"
                        type="text"
                        required
                        maxLength={30}
                        placeholder="例如：某小区消防通道长期被占用"
                        className="w-full px-4 py-3 rounded-md bg-background-100 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
                      />
                      <p className="mt-1 text-xs text-foreground-400">一句话说清事件，不超过 30 字</p>
                    </div>

                    <div>
                      <label htmlFor="category" className="block text-sm font-medium text-foreground-800 mb-1.5">
                        线索类型 <span className="text-accent-600">*</span>
                      </label>
                      <select
                        id="category"
                        name="category"
                        required
                        defaultValue=""
                        className="w-full px-4 py-3 rounded-md bg-background-100 border border-background-300 text-sm text-foreground-900 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-200 cursor-pointer"
                      >
                        <option value="" disabled>请选择对应主题</option>
                        {categories.map((item) => (
                          <option key={item} value={item}>{item}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="region" className="block text-sm font-medium text-foreground-800 mb-1.5">
                        事件发生地区
                      </label>
                      <input
                        id="region"
                        name="region"
                        type="text"
                        placeholder="例如：江苏 南京"
                        className="w-full px-4 py-3 rounded-md bg-background-100 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                        联系邮箱 <span className="text-accent-600">*</span>
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        placeholder="用于接收处理结果"
                        className="w-full px-4 py-3 rounded-md bg-background-100 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="content" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      线索详情 <span className="text-accent-600">*</span>
                    </label>
                    <textarea
                      id="content"
                      name="content"
                      required
                      maxLength={500}
                      rows={6}
                      onChange={(e) => setContentLength(e.target.value.length)}
                      placeholder="请按「时间、地点、人物、经过、诉求」的顺序描述，尽量写清可核实的要素。"
                      className="w-full px-4 py-3 rounded-md bg-background-100 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-200 resize-none"
                    ></textarea>
                    <div className="mt-1 flex items-center justify-between text-xs text-foreground-400">
                      <span>最多 500 字，超过将无法提交</span>
                      <span className={contentLength > 460 ? 'text-accent-600' : ''}>{contentLength}/500</span>
                    </div>
                  </div>

                  <input
                    type="text"
                    name="contact_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="form-guard-field"
                  />

                  <div className="flex flex-col sm:flex-row sm:items-center gap-4 pt-1">
                    <label className="flex items-center gap-2.5 text-sm text-foreground-700 cursor-pointer">
                      <input
                        type="checkbox"
                        name="anonymous"
                        value="同意匿名处理"
                        className="w-4 h-4 rounded border-background-300 text-primary-600 focus:ring-primary-300 cursor-pointer"
                      />
                      希望匿名处理本次投稿
                    </label>
                    <label className="flex items-center gap-2.5 text-sm text-foreground-700 cursor-pointer">
                      <input
                        type="checkbox"
                        name="consent"
                        required
                        value="同意编辑核实与删改"
                        className="w-4 h-4 rounded border-background-300 text-primary-600 focus:ring-primary-300 cursor-pointer"
                      />
                      同意编辑在核实后进行必要删改
                    </label>
                  </div>

                  {status === 'error' && (
                    <div className="flex items-start gap-2 p-3.5 rounded-md bg-accent-50 border border-accent-200 text-sm text-accent-800">
                      <i className="ri-error-warning-line mt-0.5 shrink-0" aria-hidden="true" />
                      <span>{errorMsg}</span>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'submitting'}
                    className={`w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-md bg-primary-500 text-background-50 font-medium whitespace-nowrap transition-colors ${
                      status === 'submitting' ? 'opacity-70 cursor-wait' : 'cursor-pointer hover:bg-primary-600'
                    }`}
                  >
                    <i className={status === 'submitting' ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-fill'} aria-hidden="true" />
                    {status === 'submitting' ? '正在提交…' : '提交投稿'}
                  </button>
                  <p className="text-center text-xs text-foreground-400">
                    提交即表示你已阅读并理解本页的格式要求与注意事项。
                  </p>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}